import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface MergeColumnsDialogProps {
  columnNames: string[];
  onConfirm: (displayName: string) => void;
  onCancel: () => void;
}

export function MergeColumnsDialog({ columnNames, onConfirm, onCancel }: MergeColumnsDialogProps) {
  const { t } = useLanguage();
  const [displayName, setDisplayName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (displayName.trim()) {
      onConfirm(displayName.trim());
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onCancel}
    >
      <div
        className="rounded-lg shadow-xl p-6 w-full max-w-md"
        style={{
          backgroundColor: 'var(--md-surface)',
          color: 'var(--md-on-surface)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{t('dialogs.mergeColumns.title')}</h2>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:bg-opacity-10"
            style={{ backgroundColor: 'var(--md-surface-variant)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-4">
          <p className="text-sm mb-3" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t('dialogs.mergeColumns.intro')}
          </p>
          <div className="flex flex-wrap gap-2 mb-4 p-3 rounded" style={{ backgroundColor: 'var(--md-surface-container)' }}>
            {columnNames.map((name, idx) => (
              <span
                key={idx}
                className="px-3 py-1 rounded text-sm font-medium"
                style={{
                  backgroundColor: 'var(--md-primary-container)',
                  color: 'var(--md-on-primary-container)',
                }}
              >
                {name}
              </span>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <label className="block mb-2 text-sm font-medium">
              {t('dialogs.mergeColumns.labelName')}
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('dialogs.mergeColumns.placeholder')}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: 'var(--md-surface-container)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
              autoFocus
            />
          </form>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded"
            style={{
              backgroundColor: 'var(--md-surface-variant)',
              color: 'var(--md-on-surface-variant)',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!displayName.trim()}
            className="px-4 py-2 rounded font-medium"
            style={{
              backgroundColor: displayName.trim() ? 'var(--md-primary)' : 'var(--md-surface-variant)',
              color: displayName.trim() ? 'var(--md-on-primary)' : 'var(--md-on-surface-variant)',
              cursor: displayName.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            {t('dialogs.mergeColumns.mergeBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
